import type { OutOfScopeDetail, RailwayApiError, RailwayResponse } from './types';

/**
 * The refusal carried by a 422 body, or null for any other 422. Keyed on the typed
 * `detail.action`, never on message text — rewording the backend's sentence must not
 * turn a refusal back into a retried failure.
 */
export const outOfScopeFrom = (status: number, body: unknown): OutOfScopeDetail | null => {
  if (status !== 422 || typeof body !== 'object' || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const { action, code, message } = detail as Record<string, unknown>;
  if (action !== 'SHOW_OUT_OF_SCOPE' || typeof code !== 'string') return null;
  return {
    code,
    action: 'SHOW_OUT_OF_SCOPE',
    ...(typeof message === 'string' ? { message } : {}),
  };
};

const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeoutMs = 10000,
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('TIMEOUT');
    }
    throw error;
  }
};

const categorize = (errorMessage: string) => {
  if (errorMessage.includes('TIMEOUT')) {
    return {
      errorCategory: 'TIMEOUT' as const,
      userMessage: 'Το σύστημα αλγορίθμου δεν απάντησε εντός 10 δευτερολέπτων (timeout).',
    };
  }
  if (errorMessage.includes('HTTP_ERROR_5')) {
    return {
      errorCategory: 'SERVER_ERROR' as const,
      userMessage: 'Το σύστημα αλγορίθμου αντιμετώπισε τεχνικό πρόβλημα (server error).',
    };
  }
  if (errorMessage.includes('HTTP_ERROR_4')) {
    return {
      errorCategory: 'CLIENT_ERROR' as const,
      userMessage: 'Μη έγκυρη αίτηση προς το σύστημα αλγορίθμου (client error).',
    };
  }
  if (errorMessage.includes('INVALID_RESPONSE')) {
    return {
      errorCategory: 'INVALID_DATA' as const,
      userMessage: 'Το σύστημα αλγορίθμου έδωσε μη έγκυρη απάντηση.',
    };
  }
  if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch failed')) {
    return {
      errorCategory: 'NETWORK_ERROR' as const,
      userMessage: 'Αδυναμία σύνδεσης με το σύστημα αλγορίθμου (network error).',
    };
  }
  return {
    errorCategory: 'UNKNOWN' as const,
    userMessage: 'Το σύστημα αλγορίθμου δεν απάντησε.',
  };
};

export interface CallRailwayOptions {
  url: string;
  payloadKey?: string; // wrapper key, e.g. 'patient_data' or 'patient' (defaults to 'patient_data')
}

/**
 * Calls the Railway scoring endpoint with timeout + 1 retry on transient
 * failures (timeouts, 5xx, network errors). Non-retryable errors and
 * exhausted retries throw a RailwayApiError with a user-presentable
 * Greek message in `userMessage` and a machine-readable `errorCategory`.
 */
export const callRailwayAPI = async (
  payload: unknown,
  options: CallRailwayOptions,
  retryCount = 0,
): Promise<RailwayResponse> => {
  const maxRetries = 1;
  const payloadKey = options.payloadKey ?? 'patient_data';

  try {
    const response = await fetchWithTimeout(
      options.url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [payloadKey]: payload }),
      },
      10000,
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error details');
      let body: unknown = null;
      try {
        body = JSON.parse(errorText);
      } catch {
        // Not JSON: an ordinary HTTP error below.
      }
      const outOfScope = outOfScopeFrom(response.status, body);
      if (outOfScope) {
        const refusal: RailwayApiError = {
          message: `OUT_OF_SCOPE: ${outOfScope.code}`,
          userMessage: 'Η περίπτωση είναι εκτός του πεδίου εφαρμογής του αλγορίθμου.',
          errorCategory: 'OUT_OF_SCOPE',
          originalError: null,
          outOfScope,
        };
        throw refusal;
      }
      throw new Error(`HTTP_ERROR_${response.status}: ${errorText}`);
    }

    const data: RailwayResponse = await response.json();

    if (data.daily_dose === null || data.daily_dose === undefined) {
      throw new Error('INVALID_RESPONSE: daily_dose is missing or null');
    }
    if (typeof data.daily_dose !== 'number' || data.daily_dose < 0) {
      throw new Error(`INVALID_RESPONSE: daily_dose is invalid (${data.daily_dose})`);
    }
    return data;
  } catch (error) {
    // A refusal is final: never retried, never re-categorised.
    if ((error as RailwayApiError | null)?.errorCategory === 'OUT_OF_SCOPE') throw error;

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    const isRetryable =
      errorMessage.includes('TIMEOUT') ||
      errorMessage.includes('HTTP_ERROR_5') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('fetch failed');

    if (retryCount < maxRetries && isRetryable) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return callRailwayAPI(payload, options, retryCount + 1);
    }

    const { errorCategory, userMessage } = categorize(errorMessage);
    const apiError: RailwayApiError = {
      message: errorMessage,
      userMessage,
      errorCategory,
      originalError: error,
    };
    throw apiError;
  }
};
